import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { AppError } from '../../api/contract';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { MetadataList } from '../../shared/detail/MetadataList';
import { approvalsRepositoryToken } from './repository';
import { targetEntityPath, type ApprovalDetail } from './types';

const STALE_PREVIEW_CODE = 'APPROVAL_PREVIEW_STALE';

export interface ApprovalExecutionDialogProps {
  readonly approval: ApprovalDetail;
  readonly dataSource: DataSource;
  readonly onClose: (completed: boolean) => void;
  /** Invoked on dialog close (not the instant staleness is detected) so the parent can refetch the approval. */
  readonly onStale: () => void;
  readonly open: boolean;
}

interface ExecutedOutcome {
  readonly executionReference: string | null;
  readonly requestId: string | null;
}

/**
 * The real `/execute` endpoint takes no request body and negotiates no client preview hash (see
 * openapi/backoffice-api.yaml) — unlike wallet adjustments there is nothing for the user to type.
 * Executing an approved change is nonetheless irreversible, so this dialog displays the approved
 * preview (action, target entity, reason, status, Request ID) and requires a genuine, distinct
 * confirmation gesture — a confirm checkbox plus a "Confirm execute" click, mirroring
 * WithdrawalRetryDialog's pattern for actions with no extra data to supply — before execute() is
 * ever called. execute() fires ONLY from that explicit click, never merely because the dialog opened.
 */
export function ApprovalExecutionDialog({
  approval,
  dataSource,
  onClose,
  onStale,
  open,
}: ApprovalExecutionDialogProps) {
  const repository = useRepository(approvalsRepositoryToken);
  const { t } = useTranslation();
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [executed, setExecuted] = useState<ExecutedOutcome | null>(null);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState<AppError | null>(null);
  const submittingRef = useRef(false);
  const idempotencyKeyRef = useRef(crypto.randomUUID());
  const previouslyOpen = useRef(open);

  useEffect(() => {
    if (open && !previouslyOpen.current) {
      submittingRef.current = false;
      setConfirmed(false);
      setBusy(false);
      setExecuted(null);
      setStale(false);
      setError(null);
      idempotencyKeyRef.current = crypto.randomUUID();
    }
    previouslyOpen.current = open;
  }, [open]);

  const blocked = busy || executed !== null || stale;

  const submit = async () => {
    if (!confirmed || blocked || submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await repository.execute(approval.id, {}, idempotencyKeyRef.current);
      setExecuted({
        executionReference: result.data.executionReference,
        requestId: result.requestId,
      });
    } catch (cause) {
      const appError = safeError(cause);
      if (appError.code === STALE_PREVIEW_CODE) {
        // Do not retry against a stale preview — the user must close (which refreshes the parent's
        // approval data via onStale) and reopen with the current state.
        setStale(true);
      } else {
        setError(appError);
      }
      submittingRef.current = false;
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (busy) return;
    // Refresh is deferred to dialog close (rather than fired the instant staleness is detected) so the
    // "refresh the latest approval" notice stays visible — the parent's resource reload synchronously
    // clears its data while refetching, which would otherwise unmount this dialog mid-message.
    if (stale) onStale();
    onClose(executed !== null);
  };

  const targetPath = targetEntityPath(approval);

  return (
    <Dialog
      aria-describedby="approval-execution-description"
      fullWidth
      maxWidth="sm"
      onClose={close}
      open={open}
    >
      <DialogTitle>{t('approvals.execution.title')}</DialogTitle>
      <DialogContent>
        <Stack id="approval-execution-description" spacing={2} sx={{ pt: 0.5 }}>
          <DataSourceBadge source={dataSource} />
          <MetadataList
            items={[
              { label: t('approvals.fields.actionType'), value: approval.actionType },
              { label: t('approvals.fields.entityType'), value: approval.entityType },
              {
                label: t('approvals.fields.entityId'),
                value: approval.entityId ?? t('common.notAvailable'),
              },
              { label: t('approvals.fields.reason'), value: approval.reason },
              { label: t('approvals.fields.status'), value: approval.status },
            ]}
          />
          <Alert severity="warning">{t('approvals.execution.impact')}</Alert>
          {busy ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <CircularProgress size={18} />
              <Typography>{t('approvals.execution.executing')}</Typography>
            </Stack>
          ) : null}
          {stale ? <Alert severity="warning">{t('approvals.execution.stale')}</Alert> : null}
          {error === null ? null : (
            <Alert severity="error">
              {error.message}
              {error.requestId === null ? null : ` Request ID: ${error.requestId}`}
            </Alert>
          )}
          {executed === null ? (
            stale ? null : (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={confirmed}
                    disabled={busy}
                    onChange={(event) => setConfirmed(event.target.checked)}
                  />
                }
                label={t('approvals.execution.confirmCheckbox')}
              />
            )
          ) : (
            <Alert severity="success">
              {t('approvals.execution.success')}
              {executed.requestId === null
                ? null
                : ` ${t('approvals.fields.requestId')}: ${executed.requestId}`}
              {targetPath === null ? null : (
                <>
                  {' '}
                  <MuiLink component={Link} to={targetPath}>
                    {t('approvals.execution.viewTarget')}
                  </MuiLink>
                </>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={close}>
          {t('actions.close')}
        </Button>
        {executed === null && !stale ? (
          <Button
            disabled={!confirmed || busy}
            loading={busy}
            onClick={() => void submit()}
            variant="contained"
          >
            {t('approvals.execution.confirm')}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

function safeError(cause: unknown): AppError {
  if (
    typeof cause === 'object' &&
    cause !== null &&
    typeof (cause as Partial<AppError>).code === 'string' &&
    'requestId' in cause
  ) {
    return cause as AppError;
  }
  return {
    status: null,
    code: 'APPROVAL_EXECUTION_FAILED',
    message: 'Unable to execute the approved change.',
    requestId: null,
    fieldErrors: {},
    retryAfterSeconds: null,
  };
}
